import {
  LEGACY_SIM_RULES,
  SIM_RULES,
  SIM_RULES_6,
  SIM_RULES_9,
  type SimRules,
} from '../sim/simRules';

/**
 * The version of the game's rules: the sim, the lawn generators, the mowers, tools, boosts and
 * tiles, and the campaign lawns (docs/ONLINE.md, "Rules versions"). A run posted online names the
 * version it was played on, and the server checks it with that version's frozen copy of the rules.
 *
 * `rules.test.ts` compares `rulesFingerprint` (`rules.ts`) with `rulesFingerprints.json`, so any change to
 * the rules fails until this number goes up and `pnpm rules:fingerprint` records the new version.
 */
export const RULES_VERSION = 15;

/**
 * The first version whose sim plays by {@link SIM_RULES_6}: a squirrel or rabbit bumped poofs away
 * (no heart lost), and a herding turn may go into a sheep. Runs on earlier versions are checked by
 * {@link LEGACY_SIM_RULES}, exactly as they were played.
 */
export const SIM_RULES_FROM = 6;

/**
 * The first version whose sim plays by {@link SIM_RULES_9}: a soccer turn may go into the ball,
 * and the Fairway's side reels reach into a secret yard. Versions 6 to 8 play by
 * {@link SIM_RULES_6}.
 */
export const SIM_RULES_9_FROM = 9;

/**
 * The first version whose sim plays by today's {@link SIM_RULES}: the Fairway's side reels in tall
 * grass slow it down. Versions 9 to 14 play by {@link SIM_RULES_9}.
 */
export const SIM_RULES_15_FROM = 15;

/** The sim rules a run on `rules` was played by. */
export function simRulesFor(rules: number): Readonly<SimRules> {
  if (rules >= SIM_RULES_15_FROM) return SIM_RULES;
  if (rules >= SIM_RULES_9_FROM) return SIM_RULES_9;
  return rules >= SIM_RULES_FROM ? SIM_RULES_6 : LEGACY_SIM_RULES;
}

/**
 * The first version whose Daily carries item boxes (docs/DAILY_ITEMS.md). Runs on earlier versions
 * are checked on the Daily without them, which is exactly the lawn they played.
 */
export const DAILY_BOXES_FROM = 5;

/** Whether a run on `rules` played the Daily with item boxes. */
export function dailyHasBoxes(rules: number): boolean {
  return rules >= DAILY_BOXES_FROM;
}

/**
 * Earlier versions whose runs the current rules check exactly, because every change since them
 * only added things (version 2 added race lawns, version 3 head-to-head lawns and matches, and
 * neither changed anything else). Version 4 changed how matches play (a bump in the side leaves a
 * mower dizzy), so version 3, which only the web game ever had, is not listed: its matches would
 * play out differently. Version 5 added item boxes to the Daily but checks a version 4 run on the
 * Daily without them (`dailyHasBoxes`), and boxes change nothing on a lawn without them, so
 * version 4 is listed. Version 6 changed the sim (`SimRules`: small critters poof, turns into
 * sheep) but checks versions 4 and 5 by their own rules (`simRulesFor`), so both are listed.
 * Version 7 only added soccer matches (docs/SOCCER_MATCH.md), so 6 is listed. Version 8 added sheep
 * round-up matches (docs/ROUNDUP_MATCH.md) and shortened a LONG soccer match, which only rooms play
 * (see {@link RETIRED_PARTS}), so 7 is listed. Version 9 changed the sim (a soccer turn into the
 * ball, side reels over a secret yard) but checks version 8 by its own rules (`simRulesFor`), and a
 * match room plays by its inviter's (`matchRulesFor`), so 8 is listed. Version 10 only added
 * hedge maze matches (docs/MAZE_MATCH.md), so 9 is listed. Version 11 only added parties of
 * three and four (docs/PARTY.md; a match of two plays as before), so 10 is listed. Version 12
 * only added the hedge maze for parties, so 11 is listed; 13 the round-up for parties, so 12;
 * 14 soccer two against two, so 13. Version 15 changed the sim (the Fairway's side reels slow it in
tall grass) but checks version 14 by its own rules (`simRulesFor`), and no match drives the
Fairway, so 14 is listed. `rules.test.ts` proves each one's recorded
 * fingerprint still matches part for part, so a version listed here needs no frozen bundle on the
 * server. A version whose rules really changed must never be listed.
 */
export const SAME_RULES_AS: readonly number[] = [14, 13, 12, 11, 10, 9, 8, 7, 6, 5, 4, 2, 1];

/**
 * Parts of the fingerprint that versions before a given one no longer play, so `rules.test.ts`
 * does not hold those versions to them. A match is judged live by its room with today's rules,
 * never posted as a run, and a room refuses a game below its mode's first version
 * (`rulesFromFor`): version 8 shortened a LONG soccer match to 70 seconds (version 7's 75 ran the
 * Zero-Turn out of gas first) and raised `SOCCER_RULES_FROM` to 8, so version 7's soccer is out of
 * service. Never retire a part a posted run is checked by.
 */
export const RETIRED_PARTS: Readonly<Record<string, number>> = { soccer: 8 };

/** Every version whose runs the current rules check: the current one and {@link SAME_RULES_AS}. */
export const RULES_CHECKED: readonly number[] = [RULES_VERSION, ...SAME_RULES_AS];

/**
 * The versions whose runs replay exactly like a run on `version`: a ghost or a challenge from any
 * of them races the same. The current version's family is {@link RULES_CHECKED}; any other version
 * is a family of its own.
 */
export function rulesFamily(version: number): readonly number[] {
  return RULES_CHECKED.includes(version) ? RULES_CHECKED : [version];
}
